import { useParams } from "react-router-dom";
import { Factory, MapPin } from "lucide-react";
import { AREAS, IMG, SERVICES } from "@/data/site";
import { usePageSeo } from "@/lib/seo";
import { CallButton, QuoteButton } from "@/components/site/Cta";
import { AreasGrid, CaseStudies, CertStrip, QuoteSection, SectionHead, ServicesGrid, Testimonials } from "@/components/site/Sections";
import { Crumbs } from "./ServicePage";
import NotFound from "./NotFound";

export default function AreaPage() {
  const { slug } = useParams();
  const a = AREAS.find((x) => x.slug === slug);
  usePageSeo(`/areas/${slug}`);
  if (!a) return <NotFound />;
  const focus = a.focus.map((f) => SERVICES.find((s) => s.slug === f)!).filter(Boolean);
  return (
    <>
      <section className="relative overflow-hidden border-b border-slate-800" data-testid={`area-page-${a.slug}`}>
        <img src={IMG.site} alt={`Commercial flooring ${a.name}`} className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F1115] via-[#0F1115]/90 to-[#0F1115]/50" />
        <div className="relative mx-auto grid max-w-7xl gap-12 px-5 py-16 md:px-8 lg:grid-cols-[1.6fr_1fr] lg:py-24">
          <div>
            <Crumbs items={[{ to: "/", label: "Home" }, { to: "/#areas", label: "Areas" }, { label: a.name }]} />
            <p className="eyebrow mb-4 flex items-center gap-2"><MapPin className="h-4 w-4" /> {a.name}{a.name.endsWith(a.state || "QLD") ? "" : ` ${a.state || "QLD"}`} {a.postcode}</p>
            <h1 className="reveal text-4xl font-extrabold uppercase leading-[0.95] text-white sm:text-5xl lg:text-6xl">{a.h1}</h1>
            <p className="mt-6 max-w-2xl text-base text-slate-300 md:text-lg">{a.intro}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <CallButton id="area-phone-cta" />
              <QuoteButton id="area-quote-cta" />
            </div>
          </div>
          <div className="self-end border border-slate-700 bg-[#0F1115]/90 p-6">
            <h2 className="eyebrow mb-4 flex items-center gap-2"><Factory className="h-4 w-4" /> Industries in {a.name}</h2>
            <ul className="space-y-2 text-white">{a.industries.map((i) => <li key={i}>— {i}</li>)}</ul>
            <h2 className="eyebrow mb-3 mt-6">Precincts covered</h2>
            <p className="text-sm text-slate-400">{a.precincts.join(" · ")}</p>
            <p className="mt-6 border-t border-slate-800 pt-4 text-sm text-slate-300">{a.local}</p>
          </div>
        </div>
      </section>
      <CertStrip />
      <section className="mx-auto max-w-7xl px-5 py-20 md:px-8">
        <SectionHead eyebrow={`Most requested in ${a.name}`} title={`Flooring systems for ${a.name}`} />
        <ServicesGrid items={focus} />
      </section>
      <section className="bg-[#161920]">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8">
          <SectionHead eyebrow="Proof" title="Recent local projects" />
          <CaseStudies />
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-5 py-20 md:px-8">
        <Testimonials />
      </section>
      <section className="mx-auto max-w-7xl px-5 pb-20 md:px-8">
        <SectionHead eyebrow="Nearby" title="Other service areas" />
        <AreasGrid exclude={a.slug} />
      </section>
      <QuoteSection title={`Flooring quote in ${a.name}`} location={a.name} />
    </>
  );
}
